import { useEffect, useState, useRef } from "react"
import { produce } from "immer"
import { combineTanStackStatuses, ERROR, OK } from "helpers/data-statuses"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { type Model, useModels } from "resources/models"
import { type Portfolio, usePortfolios } from "resources/portfolios"
import type { Preferences, PreferencesValidationOptions } from "hooks/use-preferences/types"

function validatePreferences(preferences: Preferences, models: Model[] | null, portfolios: Portfolio[] | null) {
  let isCorrected = false
  const validatedPreferences = produce(preferences, (draft: Preferences = {}) => {
    if (!models?.some(model => model.id === draft.modelId)) {
      draft.modelId = getMostUsedOrDefaultModelId(portfolios ?? [], models ?? [])
      isCorrected = true
    }
  })
  return {
    isCorrected,
    preferences: validatedPreferences,
  }
}

export function useModelPreferencesValidation() {
  const [models, modelsStatus] = useModels()
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [preferenceValidationOptions, setPreferenceValidationOptions] =
    useState<PreferencesValidationOptions | null>(null)

  const lastProcessedOptionsRef = useRef<PreferencesValidationOptions | null>(null)
  const dependenciesStatus = combineTanStackStatuses([modelsStatus, portfoliosStatus])

  useEffect(() => {
    const isReady = dependenciesStatus === OK || dependenciesStatus === ERROR
    if (!preferenceValidationOptions || !isReady) {
      return
    }

    // Single-use per options payload (no setState clear needed)
    if (lastProcessedOptionsRef.current === preferenceValidationOptions) {
      return
    }

    const { preferences, callback } = preferenceValidationOptions

    let validationResult = { isCorrected: false, preferences }
    if (dependenciesStatus === OK) {
      validationResult = validatePreferences(preferences, models, portfolios)
    }

    callback(validationResult)
    lastProcessedOptionsRef.current = preferenceValidationOptions
  }, [dependenciesStatus, preferenceValidationOptions, models, portfolios])

  return setPreferenceValidationOptions
}
